import { useEffect, useState } from 'react';

import { useWindowSize } from '@uidotdev/usehooks';
import { useTranslation } from 'react-i18next';
import { FixedSizeList } from 'react-window';
import { useSnapshot } from 'valtio';

import { useTheme } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CheckIcon from '@mui/icons-material/Check';
import Co2Icon from '@mui/icons-material/Co2';
import LanguageIcon from '@mui/icons-material/Language';
import PaymentsIcon from '@mui/icons-material/Payments';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SortIcon from '@mui/icons-material/Sort';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import { RetryIconButton } from '@/components/common';

import {
  getBookingsExpandedQueryWhereClauseFromFilters,
  store,
} from '@/components/pages/EmissionsDashboard';

import { useGetBookingsExpandedQuery } from '@/graphql/GetBookingsExpanded.generated';
import {
  Container_Insights_Bookings_Expanded_Order_By,
  Order_By,
} from '@/graphql/types';

import { useBreakpoints, useFormatters } from '@/hooks';

import { appStore, reportRequestError, retryAfterRequestError } from '@/store';

import { MapPaper } from '../MapPaper';

import { TripItem } from './TripItem';

function TripList() {
  const { t } = useTranslation();
  const theme = useTheme();
  const windowSize = useWindowSize();

  const snap = useSnapshot(store) as typeof store;
  const appSnap = useSnapshot(appStore);
  const { useDown, useOnly } = useBreakpoints();
  const lgOnly = useOnly('lg');
  const downXl = useDown('xl');

  const { formatNumber } = useFormatters();

  const height = windowSize.height
    ? windowSize.height - appSnap.appBar.height
    : 0;

  const [errorId, setErrorId] = useState<string | null>(null);

  const [anchorElSortMenu, setAnchorElSortMenu] = useState<null | HTMLElement>(
    null,
  );

  const [orderByField, setOrderByField] =
    useState<keyof Container_Insights_Bookings_Expanded_Order_By>(
      'end_datetime',
    );

  const [orderByDirection, setOrderByDirection] = useState<Order_By>(
    Order_By.DescNullsLast,
  );

  const handleOpenSortMenu = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorElSortMenu(event.currentTarget);

  const handleCloseSortMenu = () => setAnchorElSortMenu(null);

  const handleChangeDirectionClick = () => {
    setOrderByDirection(
      orderByDirection === Order_By.DescNullsLast
        ? Order_By.AscNullsFirst
        : Order_By.DescNullsLast,
    );
  };

  const handleSetOrderByFieldClick =
    (field: keyof Container_Insights_Bookings_Expanded_Order_By) => () => {
      handleCloseSortMenu();
      setOrderByField(field);
    };

  const where = getBookingsExpandedQueryWhereClauseFromFilters(
    snap as typeof store,
  );

  const [{ data, fetching, error }, reexecuteQuery] =
    useGetBookingsExpandedQuery({
      variables: {
        where,
        order_by: [{ [orderByField]: orderByDirection }],
      },
    });

  useEffect(() => {
    if (error) {
      setErrorId(reportRequestError(error, reexecuteQuery));
    }
  }, [error, reexecuteQuery]);

  const handleRetry = () => {
    retryAfterRequestError(errorId);
    setErrorId(null);
  };

  const bookings = data?.container_insights_bookings_expanded || [];

  /**
   * adapt list size
   */

  let containerHeight = height - parseFloat(theme.spacing(20));

  const totalCo2e = bookings.reduce(
    (acc, booking) => acc + booking.co2eq_kg,
    0,
  );

  return (
    <MapPaper
      sx={{
        width: 400,
        height: height - parseFloat(theme.spacing(8)),
        top: 0,
        right: theme.spacing(8),
      }}
      tooltip="Trips"
      title={
        <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
          <Typography variant="h5">
            {t('voyages')}{' '}
            <sup
              style={{
                fontSize: theme.typography.pxToRem(16),
                color: theme.palette.grey[600],
                visibility: fetching ? 'hidden' : 'visible',
              }}
            >
              ({bookings.length})
            </sup>
          </Typography>

          <IconButton size="small" onClick={handleChangeDirectionClick}>
            <SwapVertIcon />
          </IconButton>
          <Button
            size="small"
            endIcon={<SortIcon />}
            onClick={handleOpenSortMenu}
          >
            {t('sortBy')}
          </Button>
          <Menu
            open={Boolean(anchorElSortMenu)}
            anchorEl={anchorElSortMenu}
            onClose={handleCloseSortMenu}
          >
            <MenuItem
              dense
              onClick={handleSetOrderByFieldClick('end_datetime')}
            >
              <ListItemIcon
                sx={{
                  visibility:
                    orderByField === 'end_datetime' ? 'visible' : 'hidden',
                }}
              >
                <CheckIcon />
              </ListItemIcon>
              Actual Time of Arrival
            </MenuItem>
            <MenuItem dense onClick={handleSetOrderByFieldClick('co2eq_kg')}>
              <ListItemIcon
                sx={{
                  visibility:
                    orderByField === 'co2eq_kg' ? 'visible' : 'hidden',
                }}
              >
                <CheckIcon />
              </ListItemIcon>
              CO₂e emissions
            </MenuItem>
          </Menu>
        </Stack>
      }
      icon={<LanguageIcon />}
      buttonSx={{
        m: 2,
        top: 0,
        right: theme.spacing(8),
      }}
    >
      <Stack
        direction="column"
        sx={{
          pl: 1,
          overflowY: 'auto',
        }}
      >
        {fetching && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: containerHeight,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!fetching && appSnap.hasRequestError(errorId) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: containerHeight,
            }}
          >
            <Stack direction="column" alignItems="center">
              <Typography color="error">Error while fetching trips</Typography>
              <RetryIconButton onRetry={handleRetry} />
            </Stack>
          </Box>
        )}

        {!fetching && !appSnap.hasRequestError(errorId) && (
          <FixedSizeList
            height={containerHeight}
            itemSize={80}
            itemCount={bookings.length}
            width={'100%'}
            itemKey={(index) => bookings[index].internal_booking_id}
          >
            {({ index, style }) => (
              <TripItem style={style} booking={bookings[index]} />
            )}
          </FixedSizeList>
        )}
      </Stack>
    </MapPaper>
  );
}

export { TripList };
